/* =====================================================================
   sts-popups.js — the one popup system (handoff v5.1 §1.5, §5.6, §10; v6
   §3.3, §3.4, §3.6). Loaded on every page by layout/theme.liquid, after
   sts-ui.js: each popup is a <dialog> opened through STS.ui.open, so focus
   trap, focus return, Escape, backdrop, inert page and scroll lock are
   sts-ui.js's. The full API contract is in the PR that added this file.

   STS.popups
     enqueue({id, open(done), canShow(), first})  ONE popup at a time; open()
                   runs on its turn when nothing covers the page, call done()
                   once closed. Same id replaces the queued one; `first`
                   jumps the queue (the v6 retake popup).
     dialog(o)     build + open now. o: {eyebrow, title, body, details, note,
                   content (Node), buttons [{label, value, outline, note}],
                   row, closable, width, onOpen(node), onClose(value)}.
                   value = the button's, undefined for x / Esc / outside tap.
                   closable:false = no x, Esc and outside taps do nothing.
                   details: the explanation, behind "Details ▾" (v6.1 1.2);
                   note:true labels it "Note from Dr. Baumann ▾".
     close(node, value)   close a dialog() from code (e.g. on navigation).
     notice({title, body, button, first})   one-button popup, queued.
     hold(name) / release(name), busy()
     tuck(el, kind, head)  v6.1 1.2: `el` hides behind a toggle, closed.
                   kind 'd' Details link, 'n' Note from Dr. Baumann link,
                   'p' a Details pill at the end of `head` (on-page messages).
                   Static markup: button[data-sts-more=d|n|p][aria-controls].
     more(text, kind) -> a toggle + its text.   fold(root) closes them all.
     notFor(title, body)  "Not for your skin type": ! octagon + Note.
     covered()     something sits over the page (a popup, the spinner, a
                   drawer): what STS.glow waits out.
   STS.ui.toast(message, opts) lives here now; v5.1 1.5 keeps it for the one
   print-blocked message. The check mark, the spinner and the glow itself
   are assets/sts-confirm.js's (STS.confirm, STS.confirm.busy, STS.glow).

   STS.routineChanged
     show({reason, why, before, after, total, scroll, ack, glow, onClose})
     show(o).scroll: v6.2 1.6, the page never moves behind the popup; OK
                   does the one scroll to the first changed step (mid-screen,
                   only if off screen), then the glow. Nothing to glow: the
                   selector (default the Skin Profile routine) comes into view
                   instead. false: no scroll at all.
     diff(before, after) -> cards   cards(list) -> <ul>   total(steps)
     glow(ids, own, o)  STS.glow(ids, o || {scroll:false}); none while a
                   consent is due (its land() glows on confirm)
     land(ids)     the changed rows glow, after one scroll to the first when
                   it is off screen; focus to Your routine's heading
     expect(reason), ack(steps, shown, type), acked(), subscriber, onType
     changeSeen(key), seeChange(key)  localStorage sts.change_seen
   Steps: {stepId, slot, time AM|PM|BOTH, title, brand, priceMsrp|price,
   dtcVariantId|productId|id, qty, extra, key, label, inStock}.
   Events on document: sts:routine:ack {steps, shown}, sts:routine:changed.
   ===================================================================== */
(function (w, d) {
  'use strict';
  var STS = (w.STS = w.STS || {});
  if (STS.popups) return;

  function reduced() {
    return !!(w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function mk(tag, cls, txt) {
    var n = d.createElement(tag);
    if (cls) n.className = cls;
    if (txt != null) n.textContent = txt;
    return n;
  }
  function emit(name, detail) {
    try { d.dispatchEvent(new CustomEvent(name, { detail: detail })); } catch (e) { /* old engine */ }
  }
  function store(s, k, v) {
    try {
      var S = w[s];
      if (v === undefined) return S.getItem(k);
      if (v === null) S.removeItem(k); else S.setItem(k, v);
    } catch (e) { /* blocked */ }
    return null;
  }

  /* ---- styles ---------------------------------------------------------
     Injected on first use, so a page that never shows a popup pays nothing.
     Prototype v6 sizes (popup L2589-2606). Four colours are
     the AA choice at these sizes instead of the prototype hex: eyebrow
     #5F6AA0 (was #7B85B4, 3.6:1), struck product #717380 (#9A9CA6, 2.7:1),
     saving --sts-success-aa (#4F8A63, 4.1:1), step name --sts-type-ink. */
  var CSS = '' +
    '.sts-pop .sts-overlay__panel{display:block;max-width:var(--sts-pop-w,480px);width:calc(100% - 32px);max-height:calc(100dvh - 32px);overflow:auto;overscroll-behavior:contain;padding:22px;border-radius:22px;animation:stsPopIn .26s ease-out}' +
    '@keyframes stsPopIn{from{opacity:0;transform:translate(-50%,calc(-50% + 14px)) scale(.98)}}' +
    '.sts-pop__head{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;margin-bottom:8px}' +
    '.sts-pop__eyebrow{margin:0 0 3px;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#5F6AA0}' +
    '.sts-pop__title{margin:0;font:700 21px/1.25 var(--sts-font);letter-spacing:0;text-transform:none;color:var(--sts-navy);text-wrap:pretty}' +
    /* Focus lands on the title so a screen reader starts there; it is not a
       control, so no ring. Two classes: the site-wide html [class^=sts-]:focus-visible
       ring (sts-components.css, 0-2-1) outranked one class and boxed every popup title. */
    '.sts-pop .sts-pop__title:focus{outline:0}' +
    '.sts-pop__x{flex:none;display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;padding:0;border:1px solid var(--sts-line);border-radius:50%;background:var(--sts-bg);color:var(--sts-navy);cursor:pointer}' +
    '.sts-pop__x svg{width:18px;height:18px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round}' +
    '.sts-pop__body{margin:0 0 14px;font-size:14px;line-height:1.5;color:var(--sts-ink);text-wrap:pretty}' +
    '.sts-pop__list{display:flex;flex-direction:column;gap:8px;margin:0 0 14px;padding:0;list-style:none}' +
    '.sts-pop__card{border:1px solid var(--sts-line);border-radius:14px;padding:11px 13px}' +
    '.sts-pop__card p{margin:0}' +
    '.sts-pop__row{display:flex;justify-content:space-between;gap:10px;align-items:baseline}' +
    '.sts-pop__step{font-size:11.5px;font-weight:700;color:var(--sts-type-ink)}' +
    '.sts-pop__delta{font-size:12.5px;font-weight:700;white-space:nowrap;color:var(--sts-text-muted)}' +
    '.sts-pop__delta--up{color:var(--sts-danger)}.sts-pop__delta--down{color:var(--sts-success-aa)}' +
    '.sts-pop__from{font-size:13px;color:#717380;text-decoration:line-through;text-wrap:pretty}' +
    '.sts-pop__to{font-size:13.5px;font-weight:600;color:var(--sts-ink);text-wrap:pretty}' +
    '.sts-pop__total{display:flex;justify-content:space-between;gap:12px;margin:0 0 16px;padding:11px 13px;border-radius:12px;background:var(--sts-bg);font-size:13.5px}' +
    '.sts-pop__total span{color:var(--sts-text-muted)}.sts-pop__total strong{color:var(--sts-navy)}' +
    '.sts-pop__actions{display:flex;flex-wrap:wrap;gap:10px}' +
    '.sts-pop__btn{flex:1 1 100%;min-height:50px;padding:14px 24px;border:0;border-radius:999px;background:var(--sts-navy);color:#fff;font:600 15px/1.2 var(--sts-font);cursor:pointer}' +
    '.sts-pop__actions--row .sts-pop__btn{flex:1 1 170px}' +
    '.sts-pop__btn--outline{background:#fff;color:var(--sts-navy);border:1.5px solid var(--sts-navy)}' +
    '.sts-pop__note{display:block;margin-top:3px;font-size:12px;font-weight:500}' +
    '.sts-pop__btn:focus-visible,.sts-pop__x:focus-visible{outline:2px solid var(--sts-navy);outline-offset:2px}' +
    '@media (max-width:860px){.sts-pop__x{width:44px;height:44px}}' +
    /* v6.1 1.2, prototype L2592 (link), L765 / L1192 / L2157 (pill). The
       ::after takes each toggle to a 44px target without changing its look. */
    '.sts-more{margin:0 0 10px}.sts-bang-t~.sts-more{margin-top:8px}' +
    '.sts-more__b{position:relative;display:inline-flex;align-items:center;gap:5px;min-height:32px;padding:6px 0;border:0;background:none;color:var(--sts-navy);font:600 13px/1.2 var(--sts-font);text-decoration:underline;text-underline-offset:3px;cursor:pointer}' +
    '.sts-more__b::after{content:"";position:absolute;inset:-6px -4px}' +
    '.sts-more__b:focus-visible{outline:2px solid var(--sts-navy);outline-offset:2px}' +
    '.sts-more>.sts-more__p{margin:4px 0 0;font-size:13.5px;line-height:1.5;color:var(--sts-ink);text-wrap:pretty}' +
    '.sts-more__p{animation:stsMoreIn .2s ease both}.sts-more__p[hidden]{display:none}' +
    '@keyframes stsMoreIn{from{opacity:0;transform:scale(.97)}}' +
    '.sts-more__head{display:flex;justify-content:space-between;align-items:center;gap:10px;min-height:28px;color:var(--sts-navy);cursor:pointer}' +
    '.sts-more__head>:first-child{min-width:0}.sts-sub__change .sts-more__head{color:var(--sts-warn-ink)}' +
    '.sts-more__head .sts-more__b{flex:none;min-height:0;padding:5px 11px;border:1.5px solid currentColor;border-radius:999px;color:inherit;font-size:12px;text-decoration:none;white-space:nowrap}' +
    '.sts-more__head .sts-more__b::after{inset:-9px -4px}' +
    '.sts-bang-t{display:flex;align-items:center;gap:10px}.sts-bang-t--danger{color:var(--sts-danger)}' +
    '.sts-bang{flex:none;display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;background:var(--sts-danger);clip-path:var(--sts-oct-shape);color:#fff;font-size:14px;font-weight:700;line-height:1}' +
    '.sts-bang--type{width:34px;height:34px;background:var(--sts-type-deep);font-size:16px}' +
    '@media (prefers-reduced-motion:reduce){.sts-pop .sts-overlay__panel,.sts-more__p{animation:none}}';
  var cssOn = false;
  function css() {
    if (cssOn || !d.head) return;
    cssOn = true;
    var s = mk('style');
    s.id = 'sts-popups-css';
    s.textContent = CSS;
    d.head.appendChild(s);
  }

  /* ---- the queue ------------------------------------------------------
     Blockers are read off the DOM so no other file has to report in. COVER
     is what sits over the page (any open overlay, a dialog() opened outside
     the queue included, the PDP confirm, the Skin Profile swap drawer and
     remove notice); BLOCK adds what a popup must also wait for: the open
     Personalize panel, edit mode, and [data-sts-popup-block], an opt-in for
     any other surface. */
  var COVER = 'dialog[open],.sts-overlay.is-open,.sts-dlg-scrim,' +
    '[data-sts-el="drawer"]:not([hidden]),[data-sts-el="remove-modal"]:not([hidden])';
  var BLOCK = COVER + ',[data-pz-el="panel"]:not([hidden]),[data-sts-profile][data-edit],[data-sts-popup-block]:not([hidden])';
  var queue = [], cur = null, holds = {}, timer = null, seq = 0;

  function has(sel) { try { return !!d.querySelector(sel); } catch (e) { return false; } }
  function blocked() {
    for (var k in holds) { if (holds[k]) return true; }
    return has(BLOCK);
  }
  function covered() { return !!cur || has(COVER); }
  function busy() { return !!cur || blocked(); }

  /* Polled while something waits: one selector read every 400ms is cheaper
     and surer than observing every attribute on the page; release() and each
     overlay close nudge it sooner. */
  function later(ms) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(pump, ms == null ? 400 : ms);
  }
  function pump() {
    timer = null;
    if (cur || !queue.length) return;
    if (!d.body || blocked()) { later(); return; }
    for (var i = 0; i < queue.length; i++) {
      var e = queue[i];
      try { if (e.canShow && !e.canShow()) continue; } catch (err) { continue; }
      queue.splice(i, 1);
      cur = e;
      var fin = false;
      var done = function () {
        if (fin) return;
        fin = true;
        cur = null;
        later(60);
      };
      try { e.open(done); } catch (err2) { done(); }
      return;
    }
    later();
  }
  function enqueue(e) {
    if (!e || typeof e.open !== 'function') return false;
    e.id = e.id || ('sts-pop-' + (++seq));
    if (cur && cur.id === e.id) return false;
    for (var i = 0; i < queue.length; i++) {
      if (queue[i].id === e.id) { queue[i] = e; later(0); return true; }
    }
    if (e.first) queue.unshift(e); else queue.push(e);
    later(0);
    return true;
  }
  d.addEventListener('sts:close', function () { if (queue.length) later(60); });

  /* ---- one popup ------------------------------------------------------ */
  function dialog(o) {
    var UI = STS.ui;
    if (!UI || !UI.open || !d.body) { if (o.onClose) o.onClose(); return null; }
    css();
    var n = mk('dialog', 'sts-overlay sts-overlay--modal sts-pop');
    n.setAttribute('data-sts-pop', o.id || '');
    if (o.width) n.style.setProperty('--sts-pop-w', o.width + 'px');
    var tid = 'sts-pop-t' + (++seq);
    n.setAttribute('aria-labelledby', tid);
    var panel = mk('div', 'sts-overlay__panel');
    var head = mk('div', 'sts-pop__head');
    var hw = mk('div');
    if (o.eyebrow) hw.appendChild(mk('p', 'sts-pop__eyebrow', o.eyebrow));
    var h = mk('h2', 'sts-pop__title', o.title || '');
    h.id = tid;
    h.tabIndex = -1;
    hw.appendChild(h);
    head.appendChild(hw);
    var value, closing = false;
    /* Every way out comes through here, not through sts-ui.js's own Esc and
       backdrop handlers (taken over in the capture phase below), so each one
       reaches onClose with its value. The page behind never moved (v6.2 1.6:
       the popup's own scroll-behind is gone), and the unlock keeps the spot
       that was tapped where it was on screen (assets/sts-scroll.js). */
    function finish(v) {
      if (closing) return;
      closing = true;
      value = v;
      UI.close(n);
    }
    if (o.closable !== false) {
      var x = mk('button', 'sts-pop__x');
      x.type = 'button';
      x.setAttribute('aria-label', 'Close');
      x.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
      x.addEventListener('click', function () { finish(); });
      head.appendChild(x);
    }
    /* closable:false (v6 4.1, a choice is required): Esc, the dialog's cancel
       and a backdrop tap do nothing. Otherwise they close, as x does.
       A backdrop tap must also START on the backdrop: a press begun on the
       text and let go outside clicks the dialog itself (QA after #655, as
       sts-ui.js). n.__stsDown = it began in the panel (sts-consent.js reads
       it); composedPath() survives a handler redrawing the clicked node. */
    function inPanel(ev) { var a = ev.composedPath && ev.composedPath(); return a && a.length ? a.indexOf(panel) > -1 : panel.contains(ev.target); }
    var intercept = function (ev) {
      if (ev.type === 'pointerdown') { n.__stsDown = inPanel(ev); return; }
      if (ev.type === 'keydown' && ev.key !== 'Escape') return;
      if (ev.type === 'click') {
        var began = n.__stsDown;
        n.__stsDown = false;
        if (began || inPanel(ev)) return;
      }
      ev.preventDefault();
      ev.stopImmediatePropagation();
      if (o.closable !== false) finish();
    };
    n.addEventListener('pointerdown', intercept, true);
    n.addEventListener('keydown', intercept, true);
    n.addEventListener('cancel', intercept, true);
    n.addEventListener('click', intercept, true);
    /* A close the browser forces past a prevented cancel (Android back, twice):
       a required choice comes straight back; anything else closes cleanly. */
    n.addEventListener('close', function () {
      if (closing || !n.__sts) return;   // closed through sts-ui.js, not forced
      if (o.closable !== false) { finish(); return; }
      try { n.showModal(); } catch (e) { /* stays closed; the lock is sts-ui.js's */ }
    });
    panel.appendChild(head);
    if (o.body) panel.appendChild(mk('p', 'sts-pop__body', o.body));
    if (o.details) panel.appendChild(more(o.details, o.note ? 'n' : 'd'));
    if (o.content) panel.appendChild(o.content);
    var acts = mk('div', 'sts-pop__actions' + (o.row ? ' sts-pop__actions--row' : ''));
    (o.buttons || [{ label: 'OK', value: 'ok' }]).forEach(function (b) {
      var btn = mk('button', 'sts-pop__btn' + (b.outline ? ' sts-pop__btn--outline' : ''), b.label);
      btn.type = 'button';
      if (b.note) btn.appendChild(mk('span', 'sts-pop__note', b.note));
      btn.addEventListener('click', function () { finish(b.value); });
      acts.appendChild(btn);
    });
    panel.appendChild(acts);
    n.appendChild(panel);
    n.__stsFinish = finish;
    d.body.appendChild(n);
    n.addEventListener('sts:close', function onClose(ev) {
      if (ev.target !== n) return;
      n.removeEventListener('sts:close', onClose);
      if (n.parentNode) n.parentNode.removeChild(n);
      if (o.onClose) o.onClose(value);
    });
    /* Focus on the title: a screen reader starts at the top, and a long
       change list is not scrolled down to its buttons on a phone. */
    UI.open(n, { focus: h });
    if (o.onOpen) { try { o.onOpen(n); } catch (e) { /* the popup is up */ } }
    return n;
  }

  /* o.now: open at once unless something already covers the page (the Skin
     Profile's edit mode, which otherwise holds the queue until Done). */
  function notice(o) {
    o = o || {};
    function open(done) {
      dialog({
        id: o.id, eyebrow: o.eyebrow, title: o.title || o.body, body: o.title ? o.body : null,
        buttons: [{ label: o.button || 'OK', value: 'ok' }],
        onClose: function (v) { done(); if (o.onClose) o.onClose(v); }
      });
    }
    if (o.now && d.body && !covered()) { open(function () { later(60); }); return true; }
    return enqueue({ id: o.id, first: o.first, open: open });
  }

  /* ---- the one toast that is left -------------------------------------
     Moved here from assets/sts-ui.js, whose served size is capped (spec 08
     AC-29). v5.1 handoff 1.5 keeps exactly ONE toast: "Allow pop-ups for this
     site so your routine can open ready to print." Anything else the customer
     must notice is a popup (notice() above). Same API as before:
     STS.ui.toast(message, {duration:3400, role:'status'|'alert', tone, action}).
     Luis 2026-10-13: also "Added to cart" + action {label, href} link on a
     phone (assets/sts-cart-fx.js). */
  var toastHost = null, toastCur = null, toastTimer = null;
  function toast(message, opts) {
    opts = opts || {};
    if (!toastHost) {
      toastHost = d.createElement('div');
      toastHost.className = 'sts-toasts';
      /* else the results takeover hides it (sts-liquid-quiz.js) */
      toastHost.setAttribute('data-slq-keep', '');
      d.body.appendChild(toastHost);
    }
    if (toastCur) killToast();
    var t = d.createElement('div');
    t.className = 'sts-toast' + (opts.tone ? ' sts-toast--' + opts.tone : '');
    t.setAttribute('role', opts.role || (opts.tone === 'error' ? 'alert' : 'status'));
    var span = d.createElement('span');
    t.appendChild(span);

    var act = opts.action;
    if (act && act.href) {
      var a = d.createElement('a');
      a.className = 'sts-toast__action';
      a.href = act.href;
      a.textContent = act.label;
      if (act.data) a.setAttribute('data-' + act.data, '');
      t.appendChild(a);
    }

    var btn = d.createElement('button');
    btn.type = 'button';
    btn.className = 'sts-toast__close';
    btn.setAttribute('aria-label', 'Close');
    btn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>';
    btn.addEventListener('click', killToast);
    t.appendChild(btn);

    toastHost.appendChild(t);
    /* The live region has to be in the document BEFORE its text changes, or the
       announcement is silently dropped — a node inserted with role="status" and
       its text already in place is not a change to a region the screen reader
       was watching. One frame is enough. This is the whole reason "Added to
       cart" was reaching sighted users only. */
    var text = message == null ? '' : String(message);
    if (w.requestAnimationFrame) w.requestAnimationFrame(function () { span.textContent = text; });
    else setTimeout(function () { span.textContent = text; }, 0);
    toastCur = t;
    var ms = opts.duration == null ? 3400 : opts.duration;
    function arm() { if (ms > 0) toastTimer = setTimeout(killToast, ms); }
    function hold() { clearTimeout(toastTimer); }
    t.addEventListener('mouseenter', hold);
    t.addEventListener('mouseleave', arm);
    t.addEventListener('focusin', hold);
    t.addEventListener('focusout', arm);
    arm();
    try { t.dispatchEvent(new CustomEvent('sts:toast', { bubbles: true, detail: { message: message } })); } catch (e) { /* old engine */ }
    return killToast;
  }
  function killToast() {
    clearTimeout(toastTimer);
    var t = toastCur;
    if (!t) return;
    toastCur = null;
    t.classList.add('is-closing');
    /* A fading toast is invisible, so it must stop being a live region too, or
       a replacement leaves the same words in two status nodes for 200ms. */
    t.removeAttribute('role');
    t.setAttribute('aria-hidden', 'true');
    setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, reduced() ? 0 : 200);
  }

  if (STS.ui) STS.ui.toast = toast;

  /* ---- v6.1 1.2: the explanation behind a toggle -------------------------
     The title, change list, prices and buttons stay in view; the sentence
     that explains them waits behind "Details ▾" ("Hide details ▴" open),
     closed each time its popup opens (fold). The on-page messages use a
     pill at the end of their title row, and the whole row toggles. One
     delegated listener serves the toggles built here and static markup. */
  var MORE = { d: ['Details ▾', 'Hide details ▴'], n: ['Note from Dr. Baumann ▾', 'Note from Dr. Baumann ▴'], p: ['Details ▾', 'Hide ▴'] };
  function flip(b, on) {
    var p = b.__p || d.getElementById(b.getAttribute('aria-controls')), t = (MORE[b.getAttribute('data-sts-more')] || MORE.d)[on ? 1 : 0];
    b.setAttribute('aria-expanded', String(on));
    /* The arrow is drawn, not read out ("Details", expanded / collapsed). */
    b.textContent = t.slice(0, -2);
    b.appendChild(mk('span', null, t.slice(-1))).setAttribute('aria-hidden', 'true');
    if (p) p.hidden = !on;
  }
  function tuck(el, kind, head) {
    if (!el || el.__stsMore || !el.parentNode) return el;
    css();
    kind = MORE[kind] ? kind : 'd';
    var b = mk('button', 'sts-more__b'), wr = el.parentNode;
    b.type = 'button';
    b.setAttribute('data-sts-more', kind);
    el.id = el.id || 'sts-more-' + (++seq);
    b.setAttribute('aria-controls', el.id);
    b.__p = el;
    el.__stsMore = b;
    el.classList.add('sts-more__p');
    if (kind === 'p' && head && head.parentNode) {
      wr = mk('div', 'sts-more__head');
      head.parentNode.insertBefore(wr, head);
      wr.appendChild(head);
      wr.appendChild(b);
    } else {
      if (!wr.classList.contains('sts-more')) {
        wr = mk('div', 'sts-more');
        el.parentNode.insertBefore(wr, el);
        wr.appendChild(el);
      }
      wr.insertBefore(b, el);
    }
    flip(b, false);
    return el;
  }
  function more(text, kind) {
    var wr = mk('div', 'sts-more'), p = mk('p', null, text);
    wr.appendChild(p);
    tuck(p, kind);
    return wr;
  }
  function fold(root) {
    css();
    [].forEach.call((root || d).querySelectorAll('[data-sts-more]'), function (b) { flip(b, false); });
  }
  function bang(type) {
    var s = mk('span', 'sts-bang' + (type ? ' sts-bang--type' : ''), '!');
    s.setAttribute('aria-hidden', 'true');
    return s;
  }
  /* "Not for your skin type" (v6.1 1.2, prototype L2590): the title in red
     after a red ! octagon, the reason behind "Note from Dr. Baumann ▾". The
     four confirms (product page, collection, article, buy again) draw their
     own markup and hand it here. */
  function notFor(title, body) {
    if (!title || !body) return;
    css();
    title.classList.add('sts-bang-t', 'sts-bang-t--danger');
    if (!title.querySelector('.sts-bang')) title.insertBefore(bang(), title.firstChild);
    fold(tuck(body, 'n').parentNode || body);
  }
  d.addEventListener('click', function (e) {
    var t = e.target, b = t && t.closest ? t.closest('[data-sts-more]') : null, h;
    if (!b) {
      h = t && t.closest ? t.closest('.sts-more__head') : null;
      if (!h || t.closest('a,button')) return;
      b = h.querySelector('[data-sts-more]');
    }
    if (b) flip(b, b.getAttribute('aria-expanded') !== 'true');
  });

  STS.popups = {
    enqueue: enqueue, busy: busy, covered: covered, dialog: dialog, notice: notice,
    tuck: tuck, more: more, fold: fold, bang: bang, notFor: notFor,
    /* Close a dialog() from code, as if `value`'s button were pressed. */
    close: function (n, value) { if (n && n.__stsFinish) n.__stsFinish(value); },
    hold: function (name) { holds[name || 'x'] = true; },
    release: function (name) { delete holds[name || 'x']; if (queue.length) later(60); }
  };

  /* v6 3.5 "Used in more than one step" (v6.1 title; Skin Profile
     editor, PDP "Swap this step"). STS.swapBoth.ask({current, next, places,
     time}) -> Promise 'all' | 'one' | null (closed). places: every step the
     product is in, edited one first, as "morning cleanser" or {time, label};
     time: 'AM' | 'PM', the routine being edited. Opens now, outside the queue. */
  function andList(a) { return a.length < 2 ? (a[0] || '') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1]; }
  STS.swapBoth = {
    ask: function (o) {
      var per = o.time === 'PM' ? 'evening' : 'morning';
      var pl = (o.places || []).map(function (p) {
        return typeof p === 'string' ? p : (p.time === 'PM' ? 'evening ' : 'morning ') + stepCat(p, '').toLowerCase();
      });
      var n = Math.max(2, pl.length);
      return new Promise(function (res) {
        if (!STS.ui || !STS.ui.open) { res('one'); return; }
        /* v6.1 1.2 / 1.3: the buttons carry the choice; the sentence is Details. */
        dialog({
          id: 'sts-swap-both', eyebrow: 'Your routine', title: 'Used in more than one step',
          details: o.current + ' is your ' + andList(pl) + ' step.',
          row: true, width: 460,
          buttons: [{ label: n > 2 ? 'Swap all ' + n : 'Swap both', value: 'all' },
            { label: 'Just swap for ' + per, value: 'one', outline: true }],
          onClose: function (v) { res(v || null); }
        });
      });
    }
  };

  /* =====================================================================
     "Your routine changed" (v5.1 5.6, v6 3.3 / 3.4 / 3.6; SP-1, HD-3)
     ===================================================================== */
  var ACK = 'sts.routine_ack';
  var WHY_KEY = 'sts.routine_reason';
  var WHY = {
    type: 'because your skin type changed',
    profile: 'because of changes in your profile',
    size: 'because you changed your routine size',
    budget: 'to fit your budget',
    swap: 'because you swapped a step',
    step: 'because you swapped a step',
    qty: 'because you changed a quantity',
    add: 'because you added a product',
    restore: 'because you restored an earlier version',
    remove: 'because you removed a step',
    undo: 'because you put a step back'
  };
  var PERIOD = { AM: 'Morning', PM: 'Evening', BOTH: 'AM + PM' };

  function money(n) {
    var M = w.STSRoutine && w.STSRoutine.money;
    if (M && M.format) return M.format(n);
    /* v6.1 13.3: the head's STS.money. */
    return STS.money ? STS.money.format(n) : '$' + n.toFixed(2);
  }
  function str(v) { return v == null || v === '' ? null : String(v); }

  /* Never the eye placeholder "Eye Cream Specific" (STS.stepLabel). */
  function stepCat(s, dflt) {
    var C = w.STSRoutine && w.STSRoutine.core;
    return (STS.stepLabel ? STS.stepLabel(s) : s.label) || (C && C.categoryForSlot && C.categoryForSlot(s.slot)) ||
      (STS.slotFam && STS.slotFam(s.slot) === 'E' ? 'Eye Cream' : dflt);
  }

  /* One entry per step AND period: an AM + PM step is two entries holding one
     bottle, so either side changing is seen; diff() folds them back. An extra
     step (v6 3.4) is keyed by its product, never by a routine slot. */
  function norm(list) {
    var out = [], seen = {};
    (list || []).forEach(function (s) {
      if (!s) return;
      if (s.k && s.per) {
        /* Acked with the placeholder: renamed, key too. */
        if (s.cat && !stepCat({ label: s.cat }, '')) s = Object.assign({}, s, { cat: 'Eye Cream', k: s.k.split('|' + s.cat).join('|Eye Cream') });
        out.push(s);
        return;
      }
      var id = str(s.productId != null ? s.productId : (s.dtcVariantId != null ? s.dtcVariantId : (s.variantId != null ? s.variantId : s.id)));
      var cat = stepCat(s, s.extra ? '' : s.slot || 'Step');
      /* t: the name with the variant (#1055 displayTitle); v: the variant, so
         another size of the same product reads as a change (v6.1 §5.1). */
      var C = w.STSRoutine && w.STSRoutine.core;
      var base = {
        cat: cat, id: id, v: str(s.dtcVariantId != null ? s.dtcVariantId : s.variantId), sid: str(s.stepId),
        /* f: slot family, pairs a swap whose label changed; d: the category
           to SHOW ("Moisturizer", not the verb label "Moisturize"). */
        f: STS.slotFam ? STS.slotFam(s.slot) || null : null, d: (C && C.categoryForSlot && C.categoryForSlot(s.slot)) || null,
        t: s.displayTitle || s.title || null, b: s.brand || null,
        p: typeof s.priceMsrp === 'number' ? s.priceMsrp : (typeof s.price === 'number' ? s.price : null),
        q: Math.max(1, parseInt(s.qty || s.quantity, 10) || 1), x: s.inStock === false ? 1 : 0
      };
      if (s.extra) {
        out.push(Object.assign({ k: 'X|' + (s.key || id), per: 'X', ex: 1 }, base));
        return;
      }
      (s.time === 'BOTH' ? ['AM', 'PM'] : [s.time === 'PM' ? 'PM' : 'AM']).forEach(function (t) {
        var k = s.key ? s.key + '|' + t : t + '|' + cat;
        seen[k] = (seen[k] || 0) + 1;
        out.push(Object.assign({ k: k + (seen[k] > 1 ? '#' + seen[k] : ''), per: t }, base));
      });
    });
    return out;
  }
  /* STS.productName(brand, title): the one product name built from both. The
     brand is left off when the title already starts with it ("PCA Skin" +
     "PCA Skin ReBalance"), compared case-insensitively over letters and digits
     only, so punctuation and spacing do not matter; a whole-word match, so
     "Zo" is still added to "Zone Serum". */
  function productName(brand, title) {
    var b = String(brand == null ? '' : brand).trim(), t = String(title == null ? '' : title).trim();
    var c = b.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!c || !t) return t || b;
    return new RegExp('^[^a-z0-9]*' + c.split('').join('[^a-z0-9]*') + '(?![a-z0-9])', 'i').test(t) ? t : b + ' ' + t;
  }
  STS.productName = productName;
  function name(e) { return productName(e.b, e.t) || 'This product'; }
  function deltaOf(dl, note) {
    return {
      delta: dl == null || !isFinite(dl) ? '' : dl === 0 ? (note ? 'No change' : 'Same price')
        : (dl > 0 ? '+' : '−') + money(Math.abs(dl)),
      tone: dl == null || !isFinite(dl) || dl === 0 ? 'flat' : (dl > 0 ? 'up' : 'down'),
      dl: dl != null && isFinite(dl) ? dl : null
    };
  }
  function qtyOf(list, id) {
    for (var i = 0; i < list.length; i++) { if (list[i].id === id) return list[i].q; }
    return 1;
  }

  function diff(before, after) {
    var A = norm(before), B = norm(after), a = {}, b = {}, raw = [], out;
    A.forEach(function (e) { a[e.k] = e; });
    B.forEach(function (e) { b[e.k] = e; });
    B.concat(A.filter(function (e) { return !b[e.k]; })).forEach(function (e) {
      var x = a[e.k], y = b[e.k];
      var xi = x ? x.id : null, yi = y ? y.id : null;
      if (xi === yi && (!x || !y || !x.v || !y.v || x.v === y.v)) return;
      /* The other half of a shared step that changed the same way. */
      for (var i = 0; i < raw.length; i++) {
        var r = raw[i];
        if (!e.ex && r.cat === e.cat && r.per !== e.per && r.per !== 'BOTH' && r.xi === xi && r.yi === yi) {
          r.per = 'BOTH';
          r.keys.push(e.k);
          return;
        }
      }
      raw.push({ keys: [e.k], per: e.per, cat: e.cat, ex: e.ex, x: x, y: y, xi: xi, yi: yi });
    });
    /* A swap whose step label changed ("Eye Serum" -> "Eye Treatment") keys
       twice: one row gained a product, another lost one. Same step id, or the
       same period and slot family: one swap row with the net price. */
    raw.forEach(function (r) {
      if (r.ex || r.xi || !r.yi || r.gone) return;
      raw.some(function (o) {
        if (o === r || o.gone || o.ex || !o.xi || o.yi || o.per !== r.per) return false;
        if (!((r.y.sid && r.y.sid === o.x.sid) || (r.y.f && r.y.f === o.x.f))) return false;
        r.x = o.x; r.xi = o.xi; r.keys = r.keys.concat(o.keys); o.gone = 1;
        return true;
      });
    });
    raw = raw.filter(function (r) { return !r.gone && !(r.xi && r.xi === r.yi && (!r.x.v || !r.y.v || r.x.v === r.y.v)); });
    out = raw.map(function (r) {
      function elsewhere(list, id) {
        return id && list.filter(function (e) { return e.id === id && r.keys.indexOf(e.k) < 0; })[0];
      }
      var stays = elsewhere(B, r.xi), had = elsewhere(A, r.yi);
      var add = r.yi && !had ? (r.y.p == null ? NaN : r.y.p) : 0;
      var sub = r.xi && !stays ? (r.x.p == null ? NaN : r.x.p) : 0;
      var note = r.ex ? '' : had ? 'Same bottle, now AM + PM'
        : (stays && !r.yi ? 'Still in your ' + (stays.per === 'AM' ? 'morning' : 'evening') + ' routine' : '');
      return Object.assign({
        step: r.ex ? 'Extra step' + (r.cat ? ' · ' + r.cat : '') : PERIOD[r.per] + ' · ' + ((r.y || r.x).d || r.cat),
        from: r.xi ? name(r.x) : (r.ex ? 'Not in your routine' : 'No product'),
        to: (r.yi ? name(r.y) : 'Step removed') + (note ? ' · ' + note : ''),
        glow: r.yi ? (r.y.sid && !r.ex ? r.y.sid : r.yi) : null, per: r.per
      }, deltaOf(add - sub, note));
    });
    /* v6 3.3: "Quantity · [Product] × 1 → × 2", per product, with the price
       difference. A product new to the routine starts from 1. */
    var done = {};
    B.forEach(function (e) {
      if (!e.id || done[e.id]) return;
      done[e.id] = 1;
      var qa = qtyOf(A, e.id);
      if (qa === e.q) return;
      /* Glows too (v6.1 7): the row holding the product. */
      out.push(Object.assign({
        step: 'Quantity', from: name(e) + ' × ' + qa, to: name(e) + ' × ' + e.q, glow: e.id, per: e.per
      }, deltaOf(e.p == null ? null : e.p * (e.q - qa), '')));
    });
    return out;
  }

  /* Totals multiply (v6 3.3) and count extra steps; a bottle used AM and PM
     is one bottle; a sold-out product is not in the total. */
  function total(steps) {
    var seen = {}, sum = 0;
    norm(steps).forEach(function (e) {
      if (!e.id || e.x || e.p == null || seen[e.id]) return;
      seen[e.id] = 1;
      sum += e.p * e.q;
    });
    return sum;
  }

  function cards(list) {
    var ul = mk('ul', 'sts-pop__list');
    list.forEach(function (c) {
      var li = mk('li', 'sts-pop__card');
      var row = mk('p', 'sts-pop__row');
      row.appendChild(mk('span', 'sts-pop__step', c.step));
      if (c.delta) row.appendChild(mk('span', 'sts-pop__delta sts-pop__delta--' + c.tone, c.delta));
      li.appendChild(row);
      var from = mk('p', 'sts-pop__from');
      from.appendChild(mk('span', 'sts-vh', 'Was: '));
      from.appendChild(mk('del', null, c.from));
      li.appendChild(from);
      var to = mk('p', 'sts-pop__to');
      to.appendChild(mk('span', 'sts-vh', 'Now: '));
      to.appendChild(d.createTextNode(c.to));
      li.appendChild(to);
      ul.appendChild(li);
    });
    return ul;
  }

  /* ---- glow (v6 3.6, v6.2 1.6) --------------------------------------------
     The one glow is STS.glow (assets/sts-confirm.js): rows whose id is in
     `ids` glow 2s in the type colour, only once no popup covers the page, so
     it never plays behind one. What stays here is when a routine change may
     glow at all. */
  /* A subscriber's consent queued or open (id "sts-consent", sts-consent.js). */
  function consentDue() {
    if (cur && cur.id === 'sts-consent') return true;
    for (var i = 0; i < queue.length; i++) { if (queue[i].id === 'sts-consent') return true; }
    return false;
  }
  /* own: consent's land() after confirm. Any other request made while a
     consent is due is dropped (v6.1 9: glow after confirm only): the routine
     widget's own glow on a PDP swap used to play first, under the popup. */
  /* o: STS.glow's options. Without them (the routine widget, which scrolls
     its own panel) the page does not move. */
  function glow(ids, own, o) {
    if ((!own && consentDue()) || !STS.glow) return;
    STS.glow((ids || []).filter(str), o || { scroll: false });
  }
  /* v6.1 7 (Restore) and 9 (after consent), v6.2 1.6: the changed rows glow
     once the page has stopped, after one scroll to the first only when it is
     off screen, so the glow is seen, not scrolled past. None on the page:
     Your routine comes into view (My subscription's list first, else the
     Skin Profile's). No routine on the page (a product page): the glow
     alone, on the widget's rows. */
  function first(sel) {
    var parts = String(sel || '').split(','), t = null;
    for (var i = 0; i < parts.length && !t; i++) { try { t = d.querySelector(parts[i]); } catch (e) { t = null; } }
    return t;
  }
  function land(ids) {
    var t = first('[data-sts-glow-target],[data-sts-sub] .sts-sub__list,.sts-profile__routine');
    glow(ids, true, { mid: true, fallback: t, focus: t && (t.querySelector('h2') || t) });
  }

  /* ---- the acknowledged snapshot --------------------------------------
     Per customer and device, so the next person on a shared browser never
     inherits it; the staff simulator is its own owner. */
  function owner() {
    if (STS.preview && STS.preview.active) return 'sim';
    var id = null;
    try { id = (w.ShopifyAnalytics && w.ShopifyAnalytics.meta.page.customerId) || (w.__st && w.__st.cid); } catch (e) { id = null; }
    return id ? 'c' + id : 'guest';
  }
  function curType() {
    var R = STS.routine;
    return String((R && R.skinType) || (STS.state && STS.state.type) || '').toUpperCase();
  }
  function acked() {
    var s = null;
    try { s = JSON.parse(store('localStorage', ACK) || 'null'); } catch (e) { s = null; }
    return s && s.o === owner() && Array.isArray(s.s) ? s : null;
  }
  /* type: the one the steps were built for, where the page knows better than
     curType() (the quiz results page, sts-retake.js). */
  function ack(steps, shown, type) {
    if (!steps) return;
    var s = norm(steps).map(function (e) {
      return { k: e.k, per: e.per, cat: e.cat, ex: e.ex, id: e.id, v: e.v, sid: e.sid, t: e.t, b: e.b, p: e.p, q: e.q, x: e.x };
    });
    store('localStorage', ACK, JSON.stringify({ o: owner(), t: type ? String(type).toUpperCase() : curType(), at: new Date().toISOString(), s: s }));
    store('sessionStorage', WHY_KEY, null);
    emit('sts:routine:ack', { steps: s, shown: !!shown });
  }
  /* A server lastChange this customer closed on this device (10-15: the
     server's seen flag never flipped, so it showed every visit). */
  var SEEN_CHG = 'sts.change_seen';
  function changeSeen(key) {
    var v = null;
    try { v = JSON.parse(store('localStorage', SEEN_CHG) || 'null'); } catch (e) { v = null; }
    return !!key && !!v && v.o === owner() && v.k === key;
  }
  function seeChange(key) {
    if (key) store('localStorage', SEEN_CHG, JSON.stringify({ o: owner(), k: key }));
  }

  function expect(reason) {
    store('sessionStorage', WHY_KEY, JSON.stringify({ r: String(reason || ''), at: Date.now() }));
  }
  function expected() {
    var v = null;
    try { v = JSON.parse(store('sessionStorage', WHY_KEY) || 'null'); } catch (e) { v = null; }
    return v && v.r && Date.now() - v.at < 600000 ? v.r : null;
  }

  function show(o) {
    o = o || {};
    var list = diff(o.before, o.after);
    if (!list.length) { if (o.ack !== false) ack(o.after); return false; }
    var RC = STS.routineChanged;
    if (RC.subscriber && RC.subscriber()) {
      emit('sts:routine:changed', { reason: o.reason, before: o.before, after: o.after, list: list });
      return false;
    }
    return enqueue({
      id: 'sts-routine-changed',
      canShow: o.canShow,
      open: function (done) {
        var wrap = mk('div');
        wrap.appendChild(cards(list));
        /* What the customer pays, the Skin Profile's own figure (S&S applied
           while it is on), never a list-price sum next to a $340 + tax header. */
        /* `msrp`, not `list`: a `var list` here would hoist over the change
           list above and hand cards() undefined. */
        var R = STS.routine, msrp = total(o.after), paid = R && typeof R.payTotal === 'function' ? R.payTotal() : 0;
        var tot = mk('p', 'sts-pop__total');
        tot.appendChild(mk('span', null, 'Your routine now' + (o.total == null && paid > 0 && paid < msrp - 0.005 ? ' with Subscribe & Save' : '')));
        tot.appendChild(mk('strong', null, money(o.total != null ? o.total : (paid > 0 ? paid : msrp))));
        wrap.appendChild(tot);
        dialog({
          id: 'sts-routine-changed',
          eyebrow: 'Your routine',
          title: 'Your routine changed',
          details: 'Updated ' + (o.why || WHY[o.reason] || 'because you edited your routine') + '.',
          content: wrap,
          /* x, Esc, outside tap and OK are the same answer here (5.6). v6.2
             1.6: the page stayed put behind the popup; this is the one scroll. */
          onClose: function () {
            if (o.ack !== false) ack(o.after, true);
            done();
            if (o.glow !== false) {
              glow(list.map(function (c) { return c.glow; }), false, o.scroll === false ? { scroll: false }
                : { mid: true, fallback: first(o.scroll || '#my-routine,#sts-profile-routine-h,.sts-profile__routine') });
            }
            if (o.onClose) o.onClose();
          }
        });
      }
    });
  }

  STS.routineChanged = {
    show: show, diff: diff, cards: cards, total: total, glow: glow, land: land,
    ack: ack, acked: acked, expect: expect, changeSeen: changeSeen, seeChange: seeChange,
    subscriber: null, onType: null
  };

  /* ---- the Skin Profile's own changes ------------------------------------
     assets/sts-profile.js publishes STS.routine and fires sts:routine:change on
     load and on every change. Against the acknowledged snapshot:
       none stored          -> this routine is the baseline, nothing shown;
       same                 -> nothing;
       different + reason   -> the popup. The reason is expect()'s, or "type"
                               when the skin type is not the acknowledged one;
                               onType, when set (the v6 4.1 retake popup),
                               takes a type change over instead;
       different, no reason -> a direct pick (edit-mode swap, Add to my
                               routine), which confirms itself on screen: the
                               snapshot follows it.
     Staff simulator only: ?sts_reason=<reason> stands in for expect(). */
  d.addEventListener('sts:routine:change', function () {
    var R = STS.routine;
    var now = R && R.get ? R.get() : null;
    if (!now || !now.length) return;
    var snap = acked();
    if (!snap) { ack(now); return; }
    var reason = expected();
    if (!reason && owner() === 'sim') {
      try { reason = new URLSearchParams(w.location.search).get('sts_reason'); } catch (e) { reason = null; }
    }
    var type = curType();
    if (!reason && snap.t && type && snap.t !== type) {
      var RC = STS.routineChanged;
      if (typeof RC.onType === 'function') { RC.onType({ before: snap.s, after: now, from: snap.t, to: type }); return; }
      reason = 'type';
    }
    if (!reason) { if (diff(snap.s, now).length) ack(now); return; }
    show({ reason: reason, before: snap.s, after: now });
  });
})(window, document);
